export interface DocumentCollectionSelectorOptions {
    matchesComponentType?(component: object, name: string): boolean;
    matchesComponentGuid?(component: object, guid: string): boolean;
    deriveComponentGuid?(referenceId: string, fileGuid: string): string;
    referenceId?: string;
    report?(message: string): void;
}

/** Resolve an index or field qualifier; ambiguous identities never select an arbitrary member. */
export function resolveDocumentCollectionElement(
    collection: readonly unknown[], selector: string, options: DocumentCollectionSelectorOptions = {},
): unknown {
    if (/^\d+$/.test(selector)) return collection[Number(selector)];
    const qualifier = /^([A-Za-z_][A-Za-z0-9_-]*):([\s\S]*)$/.exec(selector);
    if (!qualifier) {
        options.report?.(`"${selector}" is neither an index nor a <field>:<value> qualifier`);
        return undefined;
    }
    const [, field, value] = qualifier;
    const elements = collection.filter((element): element is Record<string, unknown> =>
        element !== null && typeof element === "object");
    let matches = elements.filter(element => {
        if (field === "type") return options.matchesComponentType?.(element, value) ?? element.constructor?.name === value;
        if (field === "guid") return options.matchesComponentGuid?.(element, value)
            ?? (element.guid === value || element.needle_editor_guid === value);
        return element[field] === value;
    });
    if (!matches.length && field === "guid" && options.referenceId && options.deriveComponentGuid) {
        const derived = options.deriveComponentGuid(options.referenceId, value);
        matches = elements.filter(element => element.guid === derived);
    }
    if (matches.length > 1) {
        options.report?.(`"${selector}" matched ${matches.length} elements ? the document must name one unambiguously`);
        return undefined;
    }
    return matches[0];
}
